/**
 * 키오스크 활동 이벤트 — 순수 규칙 (vitest). 부수효과(큐·전송)는 `activity.ts`.
 *
 * 방문(visit) 경계: 어트랙트를 떠나면 시작, thanks 에 들어가거나 세션이 리셋되면 끝.
 * 화면 전환은 `screen` 이벤트 하나로 기록하고 — 들어간 화면·떠난 화면·떠난 화면 체류 시간 + 그 시점의 테마·스타일 —
 * 테마 선택·스타일 선택·촬영 확인·영상 건너뛰기 같은 퍼널 단계는 서버 집계가 전환 쌍에서 파생한다 (BOOTH-87).
 */
import { ACTIVITY_BATCH_MAX, type KioskActivityInput, type LocaleCode } from "@fiket/booth-schema";
import type { Route } from "../store/router";

/** 큐에 쌓인 뒤 늦어도 이 시간 안에 보낸다 */
export const FLUSH_AFTER_MS = 5_000;
/** 이만큼 쌓이면 즉시 보낸다 */
export const FLUSH_AT = 10;
/** 전송 실패가 이어질 때 큐 상한 — 넘치면 오래된 것부터 버린다 (통계가 손님 기기 메모리를 먹지 않게) */
export const QUEUE_CAP = 200;

export type VisitTransition = "start" | "end" | "none";

/** 라우트 전환이 방문 경계인지. attract → X = 시작, X → thanks|attract = 끝. */
export function visitTransition(prev: Route, next: Route): VisitTransition {
  if (prev === next) return "none";
  if (prev === "attract") return "start";
  if (next === "thanks" || next === "attract") return "end";
  return "none";
}

export interface TransitionContext {
  /** 전환 뒤의 방문 id (시작이면 새 id) */
  visitId: string | null;
  themeId: string | null;
  styleId: string | null;
  language: LocaleCode;
  /** 떠난 화면에 들어간 시각(ms) — 체류 시간 계산 */
  prevEnteredAt: number;
  now: number;
}

/** 라우트 전환 한 번이 만드는 이벤트들 — visit_start? · screen · visit_end? 순. 같은 화면 재진입은 없음. */
export function transitionEvents(prev: Route, next: Route, ctx: TransitionContext): KioskActivityInput[] {
  if (prev === next) return [];
  const clientAt = new Date(ctx.now).toISOString();
  const base = {
    clientAt,
    ...(ctx.visitId ? { visitId: ctx.visitId } : {}),
    language: ctx.language,
  };
  const out: KioskActivityInput[] = [];
  const kind = visitTransition(prev, next);
  if (kind === "start") out.push({ type: "visit_start", ...base, route: next });
  out.push({
    type: "screen",
    ...base,
    route: next,
    from: prev,
    durationMs: Math.max(0, Math.round(ctx.now - ctx.prevEnteredAt)),
    ...(ctx.themeId ? { themeId: ctx.themeId } : {}),
    ...(ctx.styleId ? { styleId: ctx.styleId } : {}),
  });
  if (kind === "end") out.push({ type: "visit_end", ...base, route: next, reason: next === "thanks" ? "thanks" : "home" });
  return out;
}

/** 큐 앞에서 한 배치를 꺼낸다 (원본 변경). */
export function takeBatch<T>(queue: T[], max: number = ACTIVITY_BATCH_MAX): T[] {
  return queue.splice(0, max);
}

/**
 * 지금 보낼지 (BOOTH-97) — 오프라인이면 보내지 않고 큐에 둔다.
 * 오프라인 중 fetch 는 즉시 실패하고 이벤트가 버려지므로, 네트워크가 돌아올 때까지 모아 둔다.
 */
export function shouldFlush(onLine: boolean, queueLen: number): boolean {
  return onLine && queueLen > 0;
}

/** 상한을 넘으면 오래된 것부터 버린다 (원본 변경). 버린 개수 반환. */
export function capQueue<T>(queue: T[], cap: number = QUEUE_CAP): number {
  const over = queue.length - cap;
  if (over <= 0) return 0;
  queue.splice(0, over);
  return over;
}
